import { useEffect, useState, ComponentProps } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { twMerge } from "tailwind-merge";

import { useCurrentCampaign } from "hooks/api/use-campaigns";
import { useCurrentClient } from "hooks/api/use-clients";
import { useCurrentTouchpoint } from "hooks/api/use-current-touchpoint";
import { useCurrentUser } from "hooks/api/use-current-user";
import { useTouchpointLocking } from "contexts/touchpoint-locking-context";
import { useReleaseVersionLock } from "hooks/api/use-touchpoint-versions";
import { getTouchpointVersion } from "generated/api/touchpoint-version";
import { mapTouchpointVersionDTO } from "utilities/api-mappers";

import { getTouchpointPaths } from "utilities/app-routes";

const touchpointVersionApi = getTouchpointVersion();

import { BuilderHeader } from "containers/admin/clients/touchpoint/components/builder/builder-header/builder-header";
import LockingModal from "components/modals/locking-modal/locking-modal";
import LockReleaseModal from "components/modals/lock-release-modal/lock-release-modal";

import { getTimeBetweenDates, isEditableStatus, isLocked } from "utilities";

import { Permission } from "types/auth";
import { TouchpointBuilderContent, TouchpointStatuses } from "types";

interface BuilderProps extends ComponentProps<"div"> {
  apiValue: TouchpointBuilderContent;
  contentName?: string;
  previewMode: boolean;
  permission: Permission;
  isSaved: boolean;
  setIsSaved: (isSaved: boolean) => void;
  isSaving: boolean;
}

const LOCK_TIMEOUT_MINUTES = 10;

const Builder = ({
  apiValue,
  contentName,
  previewMode,
  permission,
  isSaved,
  setIsSaved,
  isSaving,
  children,
  className,
  ...rest
}: BuilderProps) => {
  const currentUser = useCurrentUser();
  const isLockedByAnother = isLocked(apiValue, currentUser);
  const isReadonly = previewMode || isLockedByAnother || !isEditableStatus(apiValue?.status);
  const { isLockingModalOpen, setIsLockingModalOpen } = useTouchpointLocking();
  const [isLockReleaseModalOpen, setIsLockReleaseModalOpen] = useState(false);
  const currentClient = useCurrentClient();
  const currentCampaign = useCurrentCampaign();
  const currentTouchpoint = useCurrentTouchpoint();
  const versionScope = {
    clientId: currentClient.id,
    campaignId: currentCampaign.id,
    touchpointId: currentTouchpoint.id,
  };
  const releaseLockMutation = useReleaseVersionLock(versionScope);
  const navigate = useNavigate();
  const location = useLocation();
  const { touchpointPreviewPath } = getTouchpointPaths({
    clientId: currentClient.id,
    campaignId: currentCampaign.id,
    touchpointId: currentTouchpoint.id,
  });

  useEffect(() => {
    if (apiValue.status === TouchpointStatuses.PUBLISHED) {
      navigate(touchpointPreviewPath, { state: location.state });
    }
  }, [apiValue.status, location.state, navigate, touchpointPreviewPath]);

  useEffect(() => {
    // Reference to store the interval ID
    let intervalId: NodeJS.Timeout | null = null;

    const checkLockTimeout = () => {
      // Skip check if modal is already open
      if (isLockReleaseModalOpen) {
        return;
      }

      if (apiValue.lastModifiedDate) {
        const timeSinceLastModified = getTimeBetweenDates(
          new Date(apiValue.lastModifiedDate),
          new Date(),
          "minutes",
        );

        if (timeSinceLastModified > LOCK_TIMEOUT_MINUTES) {
          // Only attempt to release the lock if we currently own it
          touchpointVersionApi
            .getOneVersion({
              clientId: currentClient.id,
              campaignId: currentCampaign.id,
              touchpointId: currentTouchpoint.id,
              id: apiValue.id,
            })
            .then(mapTouchpointVersionDTO)
            .then((response) => {
              if (response.lockedBy?.id === currentUser.id) {
                releaseLockMutation
                  .mutateAsync(apiValue.id)
                  .then(() => {
                    setIsLockReleaseModalOpen(true);
                  })
                  .catch((error) => {
                    console.error("Error releasing lock:", error);
                  });
              }
            });
        }
      }
    };

    const initialCheckDelay = setTimeout(() => {
      // Set up the interval for regular checks
      intervalId = setInterval(checkLockTimeout, 6000);
    }, 5000); // 5 second initial delay

    // Clean up both the timeout and interval on unmount
    return () => {
      clearTimeout(initialCheckDelay);
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [
    apiValue.id,
    apiValue.lastModifiedDate,
    apiValue.lockedBy?.id,
    currentCampaign.id,
    currentClient.id,
    currentTouchpoint.id,
    currentUser.id,
    releaseLockMutation,
    isLockReleaseModalOpen,
  ]);

  const handleCloseLockingModal = () => {
    setIsLockingModalOpen(false);
    navigate(touchpointPreviewPath, { state: location.state });
  };

  const handleCloseLockReleaseModal = () => {
    setIsLockReleaseModalOpen(false);
    navigate(touchpointPreviewPath, { state: location.state });
  };

  return (
    <div
      id="touchpoint-builder"
      className={twMerge("flex flex-col flex-1 absolute top-0 left-0 w-full h-full", className)}
      {...rest}>
      <BuilderHeader
        apiValue={apiValue}
        contentName={contentName}
        readonly={isReadonly}
        permission={permission}
        isSaved={isSaved}
        setIsSaved={setIsSaved}
        isSaving={isSaving}
      />
      {isLockingModalOpen && (
        <LockingModal
          triggerRefresh={true}
          onCloseCallback={handleCloseLockingModal}
          cancelButtonLabel="Exit builder"
        />
      )}
      {isLockReleaseModalOpen && (
        <LockReleaseModal onCloseCallback={handleCloseLockReleaseModal} />
      )}
      {children}
    </div>
  );
};

export default Builder;
